import { summarizeMonth } from '@financego/core';
import { Plus, Repeat2, SlidersHorizontal, TrendingDown, TrendingUp, Wrench } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useI18n } from '../../i18n/useI18n';
import type { TranslationKey } from '../../i18n/messages';
import { formatMoney, formatMonth } from './dashboard-format';
import type { DashboardSnapshot } from './dashboard.types';
import { MovementCard } from './MovementCard';
import { buildPlanListItems } from './plan-data';
import type { PlanFilters } from './plan-filter.types';
import { PlanFilterSheet } from './PlanFilterSheet';

const movementFilterLabels = {
  all: 'dashboard.filterAll',
  recurring: 'dashboard.filterRecurring',
  'one-time': 'dashboard.filterOneTime',
  transfers: 'dashboard.filterTransfers',
} satisfies Record<PlanFilters['movement'], TranslationKey>;

export function PlanView({
  filtersOpen,
  onAddMovement,
  onCloseFilters,
  onOpenFilters,
  onOpenTools,
  snapshot,
}: {
  readonly filtersOpen: boolean;
  readonly onAddMovement: () => void;
  readonly onCloseFilters: () => void;
  readonly onOpenFilters: () => void;
  readonly onOpenTools: () => void;
  readonly snapshot: DashboardSnapshot;
}) {
  const initialDate = new Date(`${snapshot.today}T00:00:00.000Z`);
  const [filters, setFilters] = useState<PlanFilters>({
    month: initialDate.getUTCMonth() + 1,
    movement: 'all',
    showOccurrences: false,
    year: initialDate.getUTCFullYear(),
  });
  const { locale, t } = useI18n();
  const items = useMemo(() => buildPlanListItems(snapshot, filters), [filters, snapshot]);
  const monthSummary = useMemo(
    () =>
      summarizeMonth(snapshot.coreTransactions, filters.year, filters.month, {
        validations: snapshot.agenda,
      }),
    [filters.month, filters.year, snapshot.agenda, snapshot.coreTransactions],
  );
  const selectedMonth = formatMonth(filters.year, filters.month, locale);

  return (
    <>
      <header className="dashboard-heading heading-with-action">
        <div>
          <p className="eyebrow">{t('dashboard.myPlan')}</p>
          <h1>{t('dashboard.monthTitle')}</h1>
          <p className="plan-description">{t('dashboard.monthDescription')}</p>
        </div>
        <div className="plan-heading-actions">
          <button
            aria-label={t('plan.tools')}
            className="button button-ghost button-small plan-tools-button"
            type="button"
            onClick={onOpenTools}
          >
            <Wrench aria-hidden="true" /> <span>{t('navigation.tools')}</span>
          </button>
          <button
            className="button button-primary desktop-add-button"
            type="button"
            onClick={onAddMovement}
          >
            <Plus aria-hidden="true" /> {t('dashboard.addMovement')}
          </button>
        </div>
      </header>

      <section className="plan-hero" aria-labelledby="month-plan-title">
        <div>
          <p className="section-kicker">{selectedMonth}</p>
          <h2 id="month-plan-title">{formatMoney(monthSummary.balance, locale)}</h2>
        </div>
        <dl className="plan-stats">
          <div>
            <dt>
              <TrendingUp aria-hidden="true" /> {t('dashboard.incomes')}
            </dt>
            <dd>{formatMoney(monthSummary.income, locale)}</dd>
          </div>
          <div>
            <dt>
              <TrendingDown aria-hidden="true" /> {t('dashboard.expenses')}
            </dt>
            <dd>{formatMoney(monthSummary.expenses, locale)}</dd>
          </div>
          <div>
            <dt>
              <Repeat2 aria-hidden="true" /> {t('dashboard.recurring')}
            </dt>
            <dd>{monthSummary.recurring}</dd>
          </div>
        </dl>
      </section>

      <div className="plan-filter-summary" aria-live="polite">
        <span>{selectedMonth}</span>
        <span>{t(movementFilterLabels[filters.movement])}</span>
        {filters.showOccurrences && <span>{t('plan.showOccurrences')}</span>}
      </div>

      <section className="movement-section" aria-labelledby="movements-title">
        <div className="section-heading-row">
          <div>
            <p className="section-kicker">{t('dashboard.activity')}</p>
            <h2 id="movements-title">{t('dashboard.movements')}</h2>
          </div>
          <div className="plan-list-actions">
            <button
              className="button button-ghost button-small desktop-filter-button"
              type="button"
              onClick={onOpenFilters}
            >
              <SlidersHorizontal aria-hidden="true" /> {t('navigation.filters')}
            </button>
            <span>{items.length}</span>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="empty-state plan-empty-state">
            <h3>{t('dashboard.noMovements')}</h3>
            <p>{t('dashboard.noMovementsDescription')}</p>
            <button className="button button-primary" type="button" onClick={onAddMovement}>
              <Plus aria-hidden="true" /> {t('dashboard.addMovement')}
            </button>
          </div>
        ) : (
          <div className="movement-list">
            {items.map((item) => (
              <MovementCard
                categories={snapshot.categories}
                item={item}
                key={`${item.transaction.id}-${item.mode}-${item.date}`}
                wallets={snapshot.wallets}
              />
            ))}
          </div>
        )}
      </section>

      {filtersOpen && (
        <PlanFilterSheet filters={filters} onApply={setFilters} onClose={onCloseFilters} />
      )}
    </>
  );
}
